import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Calculator, RefreshCw, Save, Link, Ship, Anchor, Building, Landmark } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const AllTariffsSettings = ({ formData, handleChange }) => {
  const [activeSubTab, setActiveSubTab] = useState('agency');
  
  // Agency Tariff States
  const [tiers, setTiers] = useState([]);
  const [agencySettings, setAgencySettings] = useState({
    extended_stay_threshold_days: 7,
    extended_stay_period_days: 5,
    extended_stay_surcharge_percent: 20
  });
  const [loading, setLoading] = useState(true);
  const [showTierModal, setShowTierModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({
    min_nt: '',
    max_nt: '',
    base_fee_eur: '',
    description: '',
    order: 0
  });
  
  // Service Item Mapping States
  const [serviceItems, setServiceItems] = useState([]);
  const [tariffMappings, setTariffMappings] = useState({
    agency_fee_service_name: '',
    hssgm_service_name: '',
    light_dues_service_name: '',
    dto_service_name: '',
    dto_freight_service_name: '',
    overtime_service_name: '',
    transit_visa_service_name: '',
    port_authority_ordino_overtime_service_name: '',
    port_authority_lcb_overtime_service_name: '',
    port_authority_lcb_c_code_service_name: '',
    port_authority_anchoring_service_name: ''
  });
  
  // Calculator States
  const [testNT, setTestNT] = useState('');
  const [testDays, setTestDays] = useState('1');
  const [calculationResult, setCalculationResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const subTabs = [
    { id: 'agency', label: 'Acentelik Tarifesi', icon: '💰' },
    { id: 'overtime', label: 'Mesai Yolluk', icon: '⏰' },
    { id: 'transitvisa', label: 'Transit Vize', icon: '📋' },
    { id: 'portauthority', label: 'Liman Başkanlığı', icon: '⚓' },
    { id: 'hssgm', label: 'HSSGM', icon: '🏥' },
    { id: 'lightdues', label: 'Kıyı Emniyeti', icon: '🚨' },
    { id: 'dto', label: 'DTO', icon: '🏛️' },
    { id: 'motorlaunch', label: 'Motor Bot', icon: '🚤' },
    { id: 'mapping', label: 'Hizmet Eşleştirme', icon: '🔗' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      console.log('=== LOADING DATA FROM API ===');
      const [tiersRes, settingsRes, mainSettingsRes] = await Promise.all([
        api.get('/agency-tariffs/tiers'),
        api.get('/agency-tariffs/settings'),
        api.get('/settings')
      ]);
      setTiers(tiersRes.data);
      setAgencySettings(settingsRes.data);
      
      // Service items from main settings
      if (mainSettingsRes.data?.service_items) {
        setServiceItems(mainSettingsRes.data.service_items);
      }
      
      // Tariff mappings from settings
      if (mainSettingsRes.data) {
        console.log('API port_authority_anchoring_service_name:', mainSettingsRes.data.port_authority_anchoring_service_name);
        const newMappings = {
          agency_fee_service_name: mainSettingsRes.data.agency_fee_service_name || '',
          hssgm_service_name: mainSettingsRes.data.hssgm_service_name || '',
          light_dues_service_name: mainSettingsRes.data.light_dues_service_name || '',
          dto_service_name: mainSettingsRes.data.dto_service_name || '',
          dto_freight_service_name: mainSettingsRes.data.dto_freight_service_name || '',
          overtime_service_name: mainSettingsRes.data.overtime_service_name || '',
          transit_visa_service_name: mainSettingsRes.data.transit_visa_service_name || '',
          port_authority_ordino_overtime_service_name: mainSettingsRes.data.port_authority_ordino_overtime_service_name || '',
          port_authority_lcb_overtime_service_name: mainSettingsRes.data.port_authority_lcb_overtime_service_name || '',
          port_authority_lcb_c_code_service_name: mainSettingsRes.data.port_authority_lcb_c_code_service_name || '',
          port_authority_anchoring_service_name: mainSettingsRes.data.port_authority_anchoring_service_name || ''
        };
        console.log('Setting tariffMappings:', newMappings);
        setTariffMappings(newMappings);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Agency Tariff Handlers
  const handleSaveAgencySettings = async () => {
    try {
      await api.put('/agency-tariffs/settings', agencySettings);
      toast.success('Acentelik tarife ayarları kaydedildi');
    } catch (error) {
      toast.error('Ayarlar kaydedilemedi');
    }
  };

  const handleAddTier = () => {
    setEditingTier(null);
    setTierForm({ min_nt: '', max_nt: '', base_fee_eur: '', description: '', order: tiers.length + 1 });
    setShowTierModal(true);
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    setTierForm({
      min_nt: tier.min_nt || '',
      max_nt: tier.max_nt || '',
      base_fee_eur: tier.base_fee_eur || '',
      description: tier.description || '',
      order: tier.order || 0
    });
    setShowTierModal(true);
  };

  const handleDeleteTier = async (tierId) => {
    if (!window.confirm('Bu tarife kademesini silmek istediğinizden emin misiniz?')) return;
    try {
      await api.delete(`/agency-tariffs/tiers/${tierId}`);
      toast.success('Tarife kademesi silindi');
      loadData();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleSaveTier = async () => {
    if (!tierForm.min_nt || !tierForm.base_fee_eur) {
      toast.error('Min NT ve Baz Ücret zorunludur');
      return;
    }
    const data = {
      min_nt: parseFloat(tierForm.min_nt),
      max_nt: tierForm.max_nt ? parseFloat(tierForm.max_nt) : null,
      base_fee_eur: parseFloat(tierForm.base_fee_eur),
      description: tierForm.description || `${tierForm.min_nt}-${tierForm.max_nt || '+'} NT`,
      order: parseInt(tierForm.order) || 0,
      active: true
    };
    try {
      if (editingTier) {
        await api.put(`/agency-tariffs/tiers/${editingTier.id}`, data);
        toast.success('Tarife kademesi güncellendi');
      } else {
        await api.post('/agency-tariffs/tiers', data);
        toast.success('Tarife kademesi eklendi');
      }
      setShowTierModal(false);
      loadData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Örnek tarife kademeleri oluşturulsun mu?')) return;
    try {
      await api.post('/agency-tariffs/seed-defaults');
      toast.success('Varsayılan tarifeler oluşturuldu');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleCalculate = async () => {
    if (!testNT) { toast.error('Net Tonaj giriniz'); return; }
    setCalculating(true);
    try {
      const response = await api.post('/agency-tariffs/calculate', {
        net_tonnage: parseFloat(testNT),
        stay_days: parseInt(testDays) || 1
      });
      setCalculationResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hesaplama başarısız');
      setCalculationResult(null);
    } finally {
      setCalculating(false);
    }
  };

  // Save all mappings
  const handleSaveMappings = async () => {
    try {
      // Explicitly create payload object with all fields
      const payload = {
        agency_fee_service_name: tariffMappings.agency_fee_service_name || '',
        hssgm_service_name: tariffMappings.hssgm_service_name || '',
        light_dues_service_name: tariffMappings.light_dues_service_name || '',
        dto_service_name: tariffMappings.dto_service_name || '',
        dto_freight_service_name: tariffMappings.dto_freight_service_name || '',
        overtime_service_name: tariffMappings.overtime_service_name || '',
        transit_visa_service_name: tariffMappings.transit_visa_service_name || '',
        port_authority_ordino_overtime_service_name: tariffMappings.port_authority_ordino_overtime_service_name || '',
        port_authority_lcb_overtime_service_name: tariffMappings.port_authority_lcb_overtime_service_name || '',
        port_authority_lcb_c_code_service_name: tariffMappings.port_authority_lcb_c_code_service_name || '',
        port_authority_anchoring_service_name: tariffMappings.port_authority_anchoring_service_name || ''
      };
      
      console.log('Saving payload:', JSON.stringify(payload, null, 2));
      const response = await api.put('/settings', payload);
      console.log('Save response:', response.status);
      toast.success('Hizmet eşleştirmeleri kaydedildi');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Kaydetme başarısız: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Motor Launch Handlers
  const handleAddMotorLaunchRate = () => {
    const nameTr = document.getElementById('new_ml_name_tr')?.value?.trim();
    const nameEn = document.getElementById('new_ml_name_en')?.value?.trim();
    const rate = parseFloat(document.getElementById('new_ml_rate')?.value) || 0;
    if (nameTr && nameEn) {
      const newRate = { id: nameTr.toLowerCase().replace(/\s+/g, '_'), name_tr: nameTr, name_en: nameEn, rate: rate, active: true };
      handleChange('motor_launch_rates', [...(formData.motor_launch_rates || []), newRate]);
      document.getElementById('new_ml_name_tr').value = '';
      document.getElementById('new_ml_name_en').value = '';
      document.getElementById('new_ml_rate').value = '';
      toast.success('Bölge eklendi');
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Yükleniyor...</div>;
  }

  // Service dropdown component
  const ServiceSelect = ({ value, onChange, label }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">-- Hizmet Kalemi Seçin --</option>
        {serviceItems.map((service, idx) => {
          const name = typeof service === 'string' ? service : service.name;
          return <option key={idx} value={name}>{name}</option>;
        })}
      </select>
      {value && <small style={{ color: '#059669', marginTop: '0.25rem', display: 'block' }}>✓ Seçili: {value}</small>}
    </div>
  );

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              padding: '0.6rem 1rem',
              background: activeSubTab === tab.id ? '#f59e0b' : 'transparent',
              color: activeSubTab === tab.id ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.2s'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Agency Tariff Tab */}
      {activeSubTab === 'agency' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, color: '#10b981' }}>💰 Acentelik Tarifesi</h3>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                    Gemi Net Tonajına (NT) göre acentelik ücretleri. Tarife EUR, proformada USD'ye çevrilir.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {tiers.length === 0 && (
                    <button type="button" className="btn btn-secondary" onClick={handleSeedDefaults}><RefreshCw size={16} /> Örnek Yükle</button>
                  )}
                  <button type="button" className="btn btn-primary" onClick={handleAddTier}><Plus size={16} /> Kademe Ekle</button>
                </div>
              </div>
            </div>
          </div>

          {/* Tiers Table */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header" style={{ background: '#f0fdf4', borderBottom: '2px solid #10b981' }}>
              <h4 style={{ margin: 0, color: '#166534' }}>Tarife Kademeleri</h4>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {tiers.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Henüz tarife kademesi tanımlanmamış.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Sıra</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Net Tonaj Aralığı</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Baz Ücret (EUR)</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.map((tier, index) => (
                      <tr key={tier.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{ background: '#10b981', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>{tier.order || index + 1}</span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>{tier.min_nt} - {tier.max_nt !== null ? tier.max_nt : '∞'} NT</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#059669' }}>€{tier.base_fee_eur?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button type="button" className="action-btn action-btn-edit" onClick={() => handleEditTier(tier)} title="Düzenle"><Edit2 size={16} /></button>
                            <button type="button" className="action-btn action-btn-danger" onClick={() => handleDeleteTier(tier.id)} title="Sil"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Extended Stay Settings */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header" style={{ background: '#fef3c7', borderBottom: '2px solid #f59e0b' }}>
              <h4 style={{ margin: 0, color: '#92400e' }}>⏱️ Uzun Kalış Ek Ücreti</h4>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Eşik Süresi (Gün)</label>
                  <input type="number" className="form-input" value={agencySettings.extended_stay_threshold_days || 7} onChange={(e) => setAgencySettings({ ...agencySettings, extended_stay_threshold_days: parseInt(e.target.value) || 7 })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Dönem Süresi (Gün)</label>
                  <input type="number" className="form-input" value={agencySettings.extended_stay_period_days || 5} onChange={(e) => setAgencySettings({ ...agencySettings, extended_stay_period_days: parseInt(e.target.value) || 5 })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ek Ücret Oranı (%)</label>
                  <input type="number" className="form-input" value={agencySettings.extended_stay_surcharge_percent || 20} onChange={(e) => setAgencySettings({ ...agencySettings, extended_stay_surcharge_percent: parseFloat(e.target.value) || 20 })} />
                </div>
              </div>
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-primary" onClick={handleSaveAgencySettings}><Save size={16} /> Kaydet</button>
              </div>
            </div>
          </div>

          {/* Calculator */}
          <div className="card">
            <div className="card-header" style={{ background: '#dbeafe', borderBottom: '2px solid #3b82f6' }}>
              <h4 style={{ margin: 0, color: '#1e40af' }}>🧮 Hesaplama Testi</h4>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', alignItems: 'end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Net Tonaj (NT)</label>
                  <input type="number" className="form-input" value={testNT} onChange={(e) => setTestNT(e.target.value)} placeholder="Örn: 3500" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Kalış Süresi (Gün)</label>
                  <input type="number" className="form-input" value={testDays} onChange={(e) => setTestDays(e.target.value)} min="1" />
                </div>
                <button type="button" className="btn btn-primary" onClick={handleCalculate} disabled={calculating} style={{ height: '42px' }}>
                  <Calculator size={16} /> {calculating ? 'Hesaplanıyor...' : 'Hesapla'}
                </button>
              </div>
              {calculationResult && (
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <div><strong>Kademe:</strong> {calculationResult.tier_description}</div>
                    <div><strong>Baz Ücret:</strong> €{calculationResult.base_fee_eur?.toFixed(2)}</div>
                    <div><strong>Ek Ücret:</strong> €{calculationResult.extended_stay_surcharge_eur?.toFixed(2)}</div>
                    <div><strong>Toplam EUR:</strong> €{calculationResult.total_fee_eur?.toFixed(2)}</div>
                  </div>
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #bbf7d0' }}>
                    <strong>Toplam USD:</strong> <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>${calculationResult.total_fee_usd?.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mesai Yolluk Tab */}
      {activeSubTab === 'overtime' && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600, color: '#ea580c' }}>
              ⏰ Mesai Yolluk Ücreti
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Mesai saatleri dışında yapılan işlemler için alınan ücret (TL)
            </p>
            
            <div style={{ padding: '1.5rem', background: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa' }}>
              <div className="form-group" style={{ maxWidth: '300px' }}>
                <label className="form-label">Mesai Yolluk Ücreti (TRY)</label>
                <input 
                  type="number" 
                  step="0.01" 
                  className="form-input" 
                  value={formData.overtime_fee_try || 7510} 
                  onChange={(e) => handleChange('overtime_fee_try', parseFloat(e.target.value) || 0)} 
                  style={{ fontSize: '1.25rem', fontWeight: 600 }}
                />
              </div>
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#ffedd5', borderRadius: '6px', fontSize: '0.875rem' }}>
                <strong>💡 Kullanım:</strong> Bu tutar proformada "Mesai Yolluk" veya eşleştirilmiş hizmet kalemi seçildiğinde otomatik uygulanır.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transit Vize Tab */}
      {activeSubTab === 'transitvisa' && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600, color: '#4f46e5' }}>
              📋 Transit Vize Harcı
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Transit geçiş yapan gemiler için vize harcı (TL)
            </p>
            
            <div style={{ padding: '1.5rem', background: '#eef2ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
              <div className="form-group" style={{ maxWidth: '300px' }}>
                <label className="form-label">Transit Vize Harcı (TRY)</label>
                <input 
                  type="number" 
                  step="0.01" 
                  className="form-input" 
                  value={formData.transit_visa_fee_try || 9376.40} 
                  onChange={(e) => handleChange('transit_visa_fee_try', parseFloat(e.target.value) || 0)} 
                  style={{ fontSize: '1.25rem', fontWeight: 600 }}
                />
              </div>
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#e0e7ff', borderRadius: '6px', fontSize: '0.875rem' }}>
                <strong>💡 Kullanım:</strong> Bu tutar proformada "Transit Vize" veya eşleştirilmiş hizmet kalemi seçildiğinde otomatik uygulanır.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liman Başkanlığı Tab */}
      {activeSubTab === 'portauthority' && (
        <PortAuthorityTab formData={formData} handleChange={handleChange} />
      )}

      {/* HSSGM Tab */}
      {activeSubTab === 'hssgm' && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600, color: '#0369a1' }}>
              🏥 HSSGM Tahakkuku (Sanitary Dues)
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Hudut ve Sahiller Sağlık Genel Müdürlüğü - TL/NRT (Net Ton başına TL)
            </p>
            
            <div style={{ padding: '1.5rem', background: '#f0f9ff', borderRadius: '8px' }}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">a) Tam (Ticari)</label>
                  <input type="number" step="0.01" className="form-input" value={formData.hssgm_commercial} onChange={(e) => handleChange('hssgm_commercial', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">b) Kabotaj (50-250 NT)</label>
                  <input type="number" step="0.01" className="form-input" value={formData.hssgm_cabotage_small} onChange={(e) => handleChange('hssgm_cabotage_small', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">c) Kabotaj (251+ NT)</label>
                  <input type="number" step="0.01" className="form-input" value={formData.hssgm_cabotage_large} onChange={(e) => handleChange('hssgm_cabotage_large', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">d) Özel Yat</label>
                  <input type="number" step="0.01" className="form-input" value={formData.hssgm_private_yacht} onChange={(e) => handleChange('hssgm_private_yacht', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">e) Turist Gemisi</label>
                  <input type="number" step="0.01" className="form-input" value={formData.hssgm_tourist_ship} onChange={(e) => handleChange('hssgm_tourist_ship', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">f) Araştırma Gemisi</label>
                  <input type="number" step="0.01" className="form-input" value={formData.hssgm_research_ship} onChange={(e) => handleChange('hssgm_research_ship', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#dbeafe', borderRadius: '6px', fontSize: '0.875rem' }}>
              <strong>💡 Hesaplama:</strong> Gemi NRT × Tarife = HSSGM Tahakkuku (TL)
            </div>
          </div>
        </div>
      )}

      {/* Light Dues (Kıyı Emniyeti) Tab */}
      {activeSubTab === 'lightdues' && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600, color: '#b45309' }}>
              🚨 Kıyı Emniyeti Fener Ücreti (Light Dues)
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              USD/NT (Net Ton başına USD) - Liman Giriş + Çıkış için 2 kez hesaplanır
            </p>
            
            {/* Yabancı Bayraklı */}
            <div style={{ marginBottom: '1.5rem', padding: '1.5rem', background: '#fef3c7', borderRadius: '8px' }}>
              <h4 style={{ color: '#b45309', marginBottom: '1rem' }}>🌍 Yabancı Bayraklı Gemiler</h4>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Ticaret (0-800 NT)</label>
                  <input type="number" step="0.00001" className="form-input" value={formData.light_dues_foreign_commercial_0_800} onChange={(e) => handleChange('light_dues_foreign_commercial_0_800', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ticaret (800+ NT)</label>
                  <input type="number" step="0.00001" className="form-input" value={formData.light_dues_foreign_commercial_800_plus} onChange={(e) => handleChange('light_dues_foreign_commercial_800_plus', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Yolcu (0-800 NT)</label>
                  <input type="number" step="0.00001" className="form-input" value={formData.light_dues_foreign_passenger_0_800} onChange={(e) => handleChange('light_dues_foreign_passenger_0_800', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Yolcu (800+ NT)</label>
                  <input type="number" step="0.00001" className="form-input" value={formData.light_dues_foreign_passenger_800_plus} onChange={(e) => handleChange('light_dues_foreign_passenger_800_plus', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            </div>

            {/* Türk Bayraklı */}
            <div style={{ padding: '1.5rem', background: '#dcfce7', borderRadius: '8px' }}>
              <h4 style={{ color: '#16a34a', marginBottom: '1rem' }}>🇹🇷 Türk Bayraklı Gemiler</h4>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Türk (0-800 NT)</label>
                  <input type="number" step="0.00001" className="form-input" value={formData.light_dues_turkish_0_800} onChange={(e) => handleChange('light_dues_turkish_0_800', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Türk (800+ NT)</label>
                  <input type="number" step="0.00001" className="form-input" value={formData.light_dues_turkish_800_plus} onChange={(e) => handleChange('light_dues_turkish_800_plus', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Kabotaj (0-800 NT)</label>
                  <input type="number" step="0.00001" className="form-input" value={formData.light_dues_turkish_cabotage_0_800} onChange={(e) => handleChange('light_dues_turkish_cabotage_0_800', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Kabotaj (800+ NT)</label>
                  <input type="number" step="0.00001" className="form-input" value={formData.light_dues_turkish_cabotage_800_plus} onChange={(e) => handleChange('light_dues_turkish_cabotage_800_plus', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#dbeafe', borderRadius: '6px', fontSize: '0.875rem' }}>
              <strong>💡 Hesaplama:</strong> (İlk 800 NT × Tarife1) + (Kalan NT × Tarife2) × 2 (Giriş+Çıkış) = Fener Ücreti (USD)
            </div>
          </div>
        </div>
      )}

      {/* DTO Tab */}
      {activeSubTab === 'dto' && (
        <div>
          {/* Türk Bayraklı Kademeleri */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header" style={{ background: '#dcfce7', borderBottom: '2px solid #22c55e' }}>
              <h4 style={{ margin: 0, color: '#16a34a' }}>🇹🇷 Türk Bayraklı Gemi Liman Hizmet Ücreti (TRY)</h4>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f0fdf4' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #22c55e' }}>Min GT</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #22c55e' }}>Max GT</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #22c55e' }}>Diğer Gemiler (TL)</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #22c55e' }}>Yolcu/Kruvaziyer (TL)</th>
                  </tr>
                </thead>
                <tbody>
                  {(formData.dto_turkish_tiers || [
                    {"min_gt": 0, "max_gt": 500, "other_fee": 500.0, "passenger_fee": 500.0},
                    {"min_gt": 501, "max_gt": 1500, "other_fee": 1120.0, "passenger_fee": 950.0},
                    {"min_gt": 1501, "max_gt": 2500, "other_fee": 2050.0, "passenger_fee": 1750.0},
                    {"min_gt": 2501, "max_gt": 5000, "other_fee": 2800.0, "passenger_fee": 2400.0},
                    {"min_gt": 5001, "max_gt": 10000, "other_fee": 3400.0, "passenger_fee": 2900.0},
                    {"min_gt": 10001, "max_gt": 25000, "other_fee": 4000.0, "passenger_fee": 4000.0},
                    {"min_gt": 25001, "max_gt": 35000, "other_fee": 4500.0, "passenger_fee": 4500.0},
                    {"min_gt": 35001, "max_gt": 50000, "other_fee": 5000.0, "passenger_fee": 5000.0},
                    {"min_gt": 50001, "max_gt": null, "other_fee": 5300.0, "passenger_fee": 5300.0}
                  ]).map((tier, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '0.5rem 1rem' }}>
                        <input 
                          type="number" 
                          className="form-input" 
                          value={tier.min_gt || 0} 
                          onChange={(e) => {
                            const newTiers = [...(formData.dto_turkish_tiers || [])];
                            newTiers[index] = { ...newTiers[index], min_gt: parseFloat(e.target.value) || 0 };
                            handleChange('dto_turkish_tiers', newTiers);
                          }}
                          style={{ width: '90px' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem 1rem' }}>
                        <input 
                          type="number" 
                          className="form-input" 
                          value={tier.max_gt || ''} 
                          onChange={(e) => {
                            const newTiers = [...(formData.dto_turkish_tiers || [])];
                            newTiers[index] = { ...newTiers[index], max_gt: e.target.value ? parseFloat(e.target.value) : null };
                            handleChange('dto_turkish_tiers', newTiers);
                          }}
                          placeholder="∞"
                          style={{ width: '90px' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                        <input 
                          type="number" 
                          step="0.01"
                          className="form-input" 
                          value={tier.other_fee || 0} 
                          onChange={(e) => {
                            const newTiers = [...(formData.dto_turkish_tiers || [])];
                            newTiers[index] = { ...newTiers[index], other_fee: parseFloat(e.target.value) || 0 };
                            handleChange('dto_turkish_tiers', newTiers);
                          }}
                          style={{ width: '100px', textAlign: 'right', fontWeight: 600 }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                        <input 
                          type="number" 
                          step="0.01"
                          className="form-input" 
                          value={tier.passenger_fee || 0} 
                          onChange={(e) => {
                            const newTiers = [...(formData.dto_turkish_tiers || [])];
                            newTiers[index] = { ...newTiers[index], passenger_fee: parseFloat(e.target.value) || 0 };
                            handleChange('dto_turkish_tiers', newTiers);
                          }}
                          style={{ width: '100px', textAlign: 'right', fontWeight: 600 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Yabancı Bayraklı Kademeleri */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header" style={{ background: '#dbeafe', borderBottom: '2px solid #3b82f6' }}>
              <h4 style={{ margin: 0, color: '#1d4ed8' }}>🌍 Yabancı Bayraklı Gemi Liman Hizmet Ücreti (TRY)</h4>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#eff6ff' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #3b82f6' }}>Min GT</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #3b82f6' }}>Max GT</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #3b82f6' }}>Diğer Gemiler (TL)</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #3b82f6' }}>Yolcu/Kruvaziyer (TL)</th>
                  </tr>
                </thead>
                <tbody>
                  {(formData.dto_foreign_tiers || [
                    {"min_gt": 0, "max_gt": 500, "other_fee": 1400.0, "passenger_fee": 1200.0},
                    {"min_gt": 501, "max_gt": 1500, "other_fee": 2800.0, "passenger_fee": 2400.0},
                    {"min_gt": 1501, "max_gt": 2500, "other_fee": 4200.0, "passenger_fee": 3600.0},
                    {"min_gt": 2501, "max_gt": 5000, "other_fee": 4900.0, "passenger_fee": 4200.0},
                    {"min_gt": 5001, "max_gt": 10000, "other_fee": 5600.0, "passenger_fee": 4800.0},
                    {"min_gt": 10001, "max_gt": 25000, "other_fee": 6300.0, "passenger_fee": 6300.0},
                    {"min_gt": 25001, "max_gt": 35000, "other_fee": 7000.0, "passenger_fee": 7000.0},
                    {"min_gt": 35001, "max_gt": 50000, "other_fee": 7500.0, "passenger_fee": 7500.0},
                    {"min_gt": 50001, "max_gt": null, "other_fee": 8000.0, "passenger_fee": 8000.0}
                  ]).map((tier, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '0.5rem 1rem' }}>
                        <input 
                          type="number" 
                          className="form-input" 
                          value={tier.min_gt || 0} 
                          onChange={(e) => {
                            const newTiers = [...(formData.dto_foreign_tiers || [])];
                            newTiers[index] = { ...newTiers[index], min_gt: parseFloat(e.target.value) || 0 };
                            handleChange('dto_foreign_tiers', newTiers);
                          }}
                          style={{ width: '90px' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem 1rem' }}>
                        <input 
                          type="number" 
                          className="form-input" 
                          value={tier.max_gt || ''} 
                          onChange={(e) => {
                            const newTiers = [...(formData.dto_foreign_tiers || [])];
                            newTiers[index] = { ...newTiers[index], max_gt: e.target.value ? parseFloat(e.target.value) : null };
                            handleChange('dto_foreign_tiers', newTiers);
                          }}
                          placeholder="∞"
                          style={{ width: '90px' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                        <input 
                          type="number" 
                          step="0.01"
                          className="form-input" 
                          value={tier.other_fee || 0} 
                          onChange={(e) => {
                            const newTiers = [...(formData.dto_foreign_tiers || [])];
                            newTiers[index] = { ...newTiers[index], other_fee: parseFloat(e.target.value) || 0 };
                            handleChange('dto_foreign_tiers', newTiers);
                          }}
                          style={{ width: '100px', textAlign: 'right', fontWeight: 600 }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                        <input 
                          type="number" 
                          step="0.01"
                          className="form-input" 
                          value={tier.passenger_fee || 0} 
                          onChange={(e) => {
                            const newTiers = [...(formData.dto_foreign_tiers || [])];
                            newTiers[index] = { ...newTiers[index], passenger_fee: parseFloat(e.target.value) || 0 };
                            handleChange('dto_foreign_tiers', newTiers);
                          }}
                          style={{ width: '100px', textAlign: 'right', fontWeight: 600 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Navlun Hasılatı */}
          <div className="card">
            <div className="card-header" style={{ background: '#fef3c7', borderBottom: '2px solid #f59e0b' }}>
              <h4 style={{ margin: 0, color: '#b45309' }}>📦 Navlun Hasılatı Oda Payı (USD)</h4>
            </div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Yük Navlun (Tekli)</label>
                  <input type="number" step="0.01" className="form-input" value={formData.dto_freight_container_single || 700} onChange={(e) => handleChange('dto_freight_container_single', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Yük Navlun (Kombine)</label>
                  <input type="number" step="0.01" className="form-input" value={formData.dto_freight_container_combined || 400} onChange={(e) => handleChange('dto_freight_container_combined', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Motor Launch Tab */}
      {activeSubTab === 'motorlaunch' && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: 600 }}>🚤 Motor Bot Ücretleri</h3>
            
            <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #86efac' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#16a34a' }}>Yeni Bölge Ekle</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1rem', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Türkçe Ad</label>
                  <input type="text" className="form-input" placeholder="Örn: 2. Demirsahası" id="new_ml_name_tr" />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>İngilizce Ad</label>
                  <input type="text" className="form-input" placeholder="Örn: 2nd Anchorage" id="new_ml_name_en" />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Ücret (USD)</label>
                  <input type="number" step="0.01" className="form-input" placeholder="0.00" id="new_ml_rate" />
                </div>
                <button type="button" onClick={handleAddMotorLaunchRate} className="btn btn-success">+ Ekle</button>
              </div>
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', fontWeight: 600, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1rem' }}>
                <span>Türkçe Ad</span>
                <span>İngilizce Ad</span>
                <span>Ücret (USD)</span>
                <span></span>
              </div>
              {(formData.motor_launch_rates || []).map((rate, index) => (
                <div key={rate.id || index} style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1rem', alignItems: 'center' }}>
                  <input type="text" className="form-input" value={rate.name_tr} onChange={(e) => {
                    const updated = [...formData.motor_launch_rates];
                    updated[index] = { ...updated[index], name_tr: e.target.value };
                    handleChange('motor_launch_rates', updated);
                  }} />
                  <input type="text" className="form-input" value={rate.name_en} onChange={(e) => {
                    const updated = [...formData.motor_launch_rates];
                    updated[index] = { ...updated[index], name_en: e.target.value };
                    handleChange('motor_launch_rates', updated);
                  }} />
                  <input type="number" step="0.01" className="form-input" value={rate.rate} onChange={(e) => {
                    const updated = [...formData.motor_launch_rates];
                    updated[index] = { ...updated[index], rate: parseFloat(e.target.value) || 0 };
                    handleChange('motor_launch_rates', updated);
                  }} />
                  <button type="button" onClick={() => {
                    const updated = formData.motor_launch_rates.filter((_, i) => i !== index);
                    handleChange('motor_launch_rates', updated);
                  }} className="action-btn action-btn-danger"><Trash2 size={16} /></button>
                </div>
              ))}
              {(!formData.motor_launch_rates || formData.motor_launch_rates.length === 0) && (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Henüz bölge eklenmemiş</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Service Mapping Tab */}
      {activeSubTab === 'mapping' && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600 }}>
              <Link size={20} style={{ marginRight: '0.5rem' }} />
              Tarife - Hizmet Kalemi Eşleştirmesi
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
              Proforma oluştururken seçilen hizmet kalemine göre otomatik tarife hesaplaması yapmak için eşleştirme yapın.
            </p>

            {/* Acentelik Ücreti */}
            <div style={{ background: '#f0fdf4', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #86efac' }}>
              <h4 style={{ color: '#166534', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                💰 Acentelik Ücreti
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Gemi NT'sine göre acentelik tarifesinden otomatik hesaplama
              </p>
              <ServiceSelect 
                value={tariffMappings.agency_fee_service_name} 
                onChange={(val) => setTariffMappings(prev => ({ ...prev, agency_fee_service_name: val }))}
                label="Acentelik Ücreti Hizmet Kalemi"
              />
            </div>

            {/* HSSGM */}
            <div style={{ background: '#f0f9ff', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #7dd3fc' }}>
              <h4 style={{ color: '#0369a1', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🏥 HSSGM Tahakkuku
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Gemi NRT × Tarife = HSSGM (TL → USD/EUR dönüşümü)
              </p>
              <ServiceSelect 
                value={tariffMappings.hssgm_service_name} 
                onChange={(val) => setTariffMappings(prev => ({ ...prev, hssgm_service_name: val }))}
                label="HSSGM Hizmet Kalemi"
              />
            </div>

            {/* Kıyı Emniyeti */}
            <div style={{ background: '#fef3c7', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #fcd34d' }}>
              <h4 style={{ color: '#b45309', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🚨 Kıyı Emniyeti Fener Ücreti
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Gemi NT ve bayrak durumuna göre fener ücreti hesaplama
              </p>
              <ServiceSelect 
                value={tariffMappings.light_dues_service_name} 
                onChange={(val) => setTariffMappings(prev => ({ ...prev, light_dues_service_name: val }))}
                label="Fener Ücreti Hizmet Kalemi"
              />
            </div>

            {/* DTO Liman Hizmet */}
            <div style={{ background: '#f3e8ff', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #c4b5fd' }}>
              <h4 style={{ color: '#7c3aed', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🏛️ DTO Liman Hizmet Ücreti
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Gemi GT ve bayrak durumuna göre DTO liman hizmet ücreti hesaplama
              </p>
              <ServiceSelect 
                value={tariffMappings.dto_service_name} 
                onChange={(val) => setTariffMappings(prev => ({ ...prev, dto_service_name: val }))}
                label="DTO Liman Hizmet Kalemi"
              />
            </div>

            {/* DTO Navlun Oda Payı */}
            <div style={{ background: '#fef3c7', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #fcd34d' }}>
              <h4 style={{ color: '#b45309', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📦 DTO Navlun Oda Payı
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Navlun hasılatı oda payı hesaplama (USD)
              </p>
              <ServiceSelect 
                value={tariffMappings.dto_freight_service_name} 
                onChange={(val) => setTariffMappings(prev => ({ ...prev, dto_freight_service_name: val }))}
                label="DTO Navlun Oda Payı Hizmet Kalemi"
              />
            </div>

            {/* Mesai Yolluk */}
            <div style={{ background: '#fff7ed', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #fed7aa' }}>
              <h4 style={{ color: '#ea580c', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                ⏰ Mesai Yolluk
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Sabit mesai yolluk ücreti (TL → USD/EUR dönüşümü)
              </p>
              <ServiceSelect 
                value={tariffMappings.overtime_service_name} 
                onChange={(val) => setTariffMappings(prev => ({ ...prev, overtime_service_name: val }))}
                label="Mesai Yolluk Hizmet Kalemi"
              />
            </div>

            {/* Transit Vize */}
            <div style={{ background: '#eef2ff', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #c7d2fe' }}>
              <h4 style={{ color: '#4f46e5', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📋 Transit Vize Harcı
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Sabit transit vize harcı (TL → USD/EUR dönüşümü)
              </p>
              <ServiceSelect 
                value={tariffMappings.transit_visa_service_name} 
                onChange={(val) => setTariffMappings(prev => ({ ...prev, transit_visa_service_name: val }))}
                label="Transit Vize Hizmet Kalemi"
              />
            </div>

            {/* Liman Başkanlığı Bölümü */}
            <div style={{ background: '#ecfeff', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #a5f3fc' }}>
              <h4 style={{ color: '#0891b2', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                ⚓ Liman Başkanlığı Ücretleri
              </h4>
              
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '0.5rem' }}>📝 Ordino Fazla Mesai</p>
                <ServiceSelect 
                  value={tariffMappings.port_authority_ordino_overtime_service_name} 
                  onChange={(val) => setTariffMappings(prev => ({ ...prev, port_authority_ordino_overtime_service_name: val }))}
                  label=""
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '0.5rem' }}>⏱️ LÇB Fazla Mesai</p>
                <ServiceSelect 
                  value={tariffMappings.port_authority_lcb_overtime_service_name} 
                  onChange={(val) => setTariffMappings(prev => ({ ...prev, port_authority_lcb_overtime_service_name: val }))}
                  label=""
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '0.5rem' }}>🔢 LÇB C Kodu</p>
                <ServiceSelect 
                  value={tariffMappings.port_authority_lcb_c_code_service_name} 
                  onChange={(val) => setTariffMappings(prev => ({ ...prev, port_authority_lcb_c_code_service_name: val }))}
                  label=""
                />
              </div>
              
              <div>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '0.5rem' }}>⚓ Demirleme Ücreti</p>
                <ServiceSelect 
                  value={tariffMappings.port_authority_anchoring_service_name} 
                  onChange={(val) => setTariffMappings(prev => ({ ...prev, port_authority_anchoring_service_name: val }))}
                  label=""
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-primary" onClick={handleSaveMappings}>
                <Save size={16} /> Tüm Eşleştirmeleri Kaydet
              </button>
            </div>

            <div style={{ marginTop: '1.5rem', background: '#fef9c3', padding: '1rem', borderRadius: '8px', fontSize: '0.875rem' }}>
              <strong>💡 Nasıl çalışır?</strong>
              <ul style={{ margin: '0.5rem 0 0 1rem', paddingLeft: 0 }}>
                <li>Proforma oluştururken gemi seçildiğinde NT ve bayrak bilgisi alınır</li>
                <li>Eşleştirilmiş hizmet kalemi seçildiğinde ilgili tarife otomatik hesaplanır</li>
                <li>Hesaplanan tutar birim fiyata yazılır, kullanıcı isterse değiştirebilir</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tier Modal */}
      {showTierModal && (
        <div className="modal-overlay" onClick={() => setShowTierModal(false)}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingTier ? 'Kademe Düzenle' : 'Yeni Kademe Ekle'}</h3>
              <button type="button" className="modal-close" onClick={() => setShowTierModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Minimum Net Tonaj *</label>
                <input type="number" className="form-input" value={tierForm.min_nt} onChange={(e) => setTierForm({ ...tierForm, min_nt: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Maksimum Net Tonaj</label>
                <input type="number" className="form-input" value={tierForm.max_nt} onChange={(e) => setTierForm({ ...tierForm, max_nt: e.target.value })} placeholder="Boş = ve üzeri" />
              </div>
              <div className="form-group">
                <label className="form-label">Baz Ücret (EUR) *</label>
                <input type="number" className="form-input" value={tierForm.base_fee_eur} onChange={(e) => setTierForm({ ...tierForm, base_fee_eur: e.target.value })} step="0.01" required />
              </div>
              <div className="form-group">
                <label className="form-label">Açıklama</label>
                <input type="text" className="form-input" value={tierForm.description} onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Sıra No</label>
                <input type="number" className="form-input" value={tierForm.order} onChange={(e) => setTierForm({ ...tierForm, order: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowTierModal(false)}>İptal</button>
              <button type="button" className="btn btn-primary" onClick={handleSaveTier}><Save size={16} /> {editingTier ? 'Güncelle' : 'Ekle'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Port Authority Tab Component
const PortAuthorityTab = ({ formData, handleChange }) => {
  const [testNT, setTestNT] = useState('');
  const [testGT, setTestGT] = useState('');
  const [testDays, setTestDays] = useState('5');
  const [testFlag, setTestFlag] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [anchoringResult, setAnchoringResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [lcbTiers, setLcbTiers] = useState([
    { min_nt: 11, max_nt: 500, fee_try: 200 },
    { min_nt: 501, max_nt: 2000, fee_try: 500 },
    { min_nt: 2001, max_nt: 4000, fee_try: 1200 },
    { min_nt: 4001, max_nt: 8000, fee_try: 1800 },
    { min_nt: 8001, max_nt: 10000, fee_try: 2400 },
    { min_nt: 10001, max_nt: 30000, fee_try: 4800 },
    { min_nt: 30001, max_nt: 50000, fee_try: 7200 },
    { min_nt: 50001, max_nt: null, fee_try: 12000 }
  ]);

  useEffect(() => {
    if (formData.lcb_overtime_tiers && formData.lcb_overtime_tiers.length > 0) {
      setLcbTiers(formData.lcb_overtime_tiers);
    }
  }, [formData.lcb_overtime_tiers]);

  const handleTierChange = (index, field, value) => {
    const updated = [...lcbTiers];
    updated[index] = { ...updated[index], [field]: value === '' ? null : parseFloat(value) };
    setLcbTiers(updated);
    handleChange('lcb_overtime_tiers', updated);
  };

  const handleCalculate = async () => {
    if (!testNT) { toast.error('Net Tonaj giriniz'); return; }
    setCalculating(true);
    try {
      // LÇB/Ordino ve C Kodu hesaplamalarını paralel yap
      const [lcbResponse, cCodeResponse] = await Promise.all([
        api.post(`/port-authority/calculate-lcb-overtime?net_tonnage=${testNT}`),
        api.post(`/port-authority/calculate-lcb-c-code?net_tonnage=${testNT}`)
      ]);
      setCalcResult({
        ...lcbResponse.data,
        lcb_c_code_try: cCodeResponse.data.lcb_c_code_try,
        lcb_c_code_usd: cCodeResponse.data.lcb_c_code_usd,
        c_code_tier: cCodeResponse.data.tier_description
      });
    } catch (error) {
      toast.error('Hesaplama başarısız');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600, color: '#0891b2' }}>
            ⚓ Liman Başkanlığı Ücretleri
          </h3>
          <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
            LÇB Fazla Mesai: Net Ton bazlı kademeli tarife | Ordino Fazla Mesai = LÇB / 2
          </p>
        </div>
      </div>

      {/* LÇB Fazla Mesai Kademeleri */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ background: '#f0fdfa', borderBottom: '2px solid #14b8a6' }}>
          <h4 style={{ margin: 0, color: '#0d9488' }}>⏱️ LÇB Fazla Mesai Kademeleri (Net Ton Bazlı)</h4>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Min NT</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Max NT</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>LÇB Ücreti (TL)</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Ordino (LÇB/2)</th>
              </tr>
            </thead>
            <tbody>
              {lcbTiers.map((tier, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '0.5rem 1rem' }}>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={tier.min_nt || ''} 
                      onChange={(e) => handleTierChange(index, 'min_nt', e.target.value)}
                      style={{ width: '100px' }}
                    />
                  </td>
                  <td style={{ padding: '0.5rem 1rem' }}>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={tier.max_nt || ''} 
                      onChange={(e) => handleTierChange(index, 'max_nt', e.target.value)}
                      placeholder="∞"
                      style={{ width: '100px' }}
                    />
                  </td>
                  <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={tier.fee_try || ''} 
                      onChange={(e) => handleTierChange(index, 'fee_try', e.target.value)}
                      style={{ width: '120px', textAlign: 'right', fontWeight: 600 }}
                    />
                  </td>
                  <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#64748b' }}>
                    {((tier.fee_try || 0) / 2).toLocaleString('tr-TR')} TL
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hesaplama Testi */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ background: '#dbeafe', borderBottom: '2px solid #3b82f6' }}>
          <h4 style={{ margin: 0, color: '#1e40af' }}>🧮 Hesaplama Testi</h4>
        </div>
        <div className="card-body">
          {/* LÇB/Ordino/C Kodu Testi */}
          <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e2e8f0' }}>
            <h5 style={{ marginBottom: '0.75rem', color: '#0d9488' }}>LÇB / Ordino / C Kodu Hesaplama</h5>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ marginBottom: 0, flex: 1, maxWidth: '200px' }}>
                <label className="form-label">Net Tonaj (NT)</label>
                <input type="number" className="form-input" value={testNT} onChange={(e) => setTestNT(e.target.value)} placeholder="Örn: 5000" />
              </div>
              <button type="button" className="btn btn-primary" onClick={handleCalculate} disabled={calculating} style={{ height: '42px' }}>
                <Calculator size={16} /> {calculating ? '...' : 'Hesapla'}
              </button>
            </div>
            {calcResult && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.875rem' }}>
                  <div><strong>Net Tonaj:</strong> {calcResult.net_tonnage} NT</div>
                  <div><strong>Kademe:</strong> {calcResult.tier_description}</div>
                  <div><strong>LÇB Fazla Mesai:</strong> <span style={{ color: '#059669', fontWeight: 600 }}>{calcResult.lcb_overtime_try?.toLocaleString('tr-TR')} TL</span></div>
                  <div><strong>Ordino Fazla Mesai:</strong> <span style={{ color: '#0891b2', fontWeight: 600 }}>{calcResult.ordino_overtime_try?.toLocaleString('tr-TR')} TL</span></div>
                  <div><strong>LÇB C Kodu:</strong> <span style={{ color: '#7c3aed', fontWeight: 600 }}>{calcResult.lcb_c_code_try?.toLocaleString('tr-TR')} TL</span></div>
                  <div><strong>C Kodu (USD):</strong> ${calcResult.lcb_c_code_usd}</div>
                  <div><strong>LÇB (USD):</strong> ${calcResult.lcb_overtime_usd}</div>
                  <div><strong>Ordino (USD):</strong> ${calcResult.ordino_overtime_usd}</div>
                </div>
              </div>
            )}
          </div>

          {/* Demirleme Ücreti Testi */}
          <div>
            <h5 style={{ marginBottom: '0.75rem', color: '#b45309' }}>⚓ Demirleme Ücreti Hesaplama (TBDTDY Hariç)</h5>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, maxWidth: '150px' }}>
                <label className="form-label">Gross Tonaj (GT)</label>
                <input type="number" className="form-input" value={testGT} onChange={(e) => setTestGT(e.target.value)} placeholder="Örn: 10000" />
              </div>
              <div className="form-group" style={{ marginBottom: 0, maxWidth: '100px' }}>
                <label className="form-label">Gün Sayısı</label>
                <input type="number" className="form-input" value={testDays} onChange={(e) => setTestDays(e.target.value)} placeholder="5" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Bayrak</label>
                <select className="form-input" value={testFlag ? 'turkish' : 'foreign'} onChange={(e) => setTestFlag(e.target.value === 'turkish')}>
                  <option value="foreign">Yabancı Bayraklı</option>
                  <option value="turkish">Türk Bayraklı</option>
                </select>
              </div>
              <button 
                className="btn btn-primary" 
                onClick={async () => {
                  if (!testGT || !testDays) { toast.error('GT ve gün sayısı giriniz'); return; }
                  setCalculating(true);
                  try {
                    const response = await api.post(`/port-authority/calculate-anchoring?gross_tonnage=${testGT}&days=${testDays}&is_turkish_flag=${testFlag}`);
                    setAnchoringResult(response.data);
                  } catch (error) {
                    toast.error('Hesaplama başarısız');
                  } finally {
                    setCalculating(false);
                  }
                }} 
                disabled={calculating} 
                style={{ height: '42px' }}
              >
                <Calculator size={16} /> {calculating ? '...' : 'Hesapla'}
              </button>
            </div>
            {anchoringResult && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong style={{ fontSize: '1rem' }}>{anchoringResult.flag_text}</strong>
                  <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>({anchoringResult.gross_tonnage} GT, {anchoringResult.days} gün)</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.875rem' }}>
                  <div><strong>Günlük Ücret (≤7 gün):</strong> ${anchoringResult.rate_under_168h}/ton</div>
                  <div><strong>Günlük Ücret (&gt;7 gün):</strong> ${anchoringResult.rate_over_168h}/ton</div>
                  <div><strong>Günlük Tutar:</strong> ${anchoringResult.daily_fee_usd}</div>
                  <div><strong>Kur:</strong> {anchoringResult.exchange_rate_used} TL</div>
                </div>
                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f59e0b' }}>
                  {anchoringResult.calculation_breakdown?.map((line, idx) => (
                    <div key={idx} style={{ fontSize: '0.8rem', color: '#78350f' }}>{line}</div>
                  ))}
                </div>
                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f59e0b', display: 'flex', gap: '2rem' }}>
                  <div><strong>Toplam (USD):</strong> <span style={{ color: '#059669', fontWeight: 700, fontSize: '1.1rem' }}>${anchoringResult.total_fee_usd}</span></div>
                  <div><strong>Toplam (TRY):</strong> <span style={{ color: '#b45309', fontWeight: 700, fontSize: '1.1rem' }}>{anchoringResult.total_fee_try?.toLocaleString('tr-TR')} TL</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LÇB C Kodu Kademeleri */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ background: '#f3e8ff', borderBottom: '2px solid #7c3aed' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0, color: '#5b21b6' }}>🔢 LÇB C Kodu Tarife Kademeleri</h4>
            <button 
              className="btn btn-primary" 
              onClick={() => handleChange('lcb_c_code_tiers', [
                ...(formData.lcb_c_code_tiers || []),
                { min_nt: '', max_nt: '', fee_try: 0 }
              ])}
              style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
            >
              <Plus size={14} /> Kademe Ekle
            </button>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#faf5ff' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #7c3aed' }}>Min NT</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #7c3aed' }}>Max NT</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #7c3aed' }}>C Kodu Ücreti (TL)</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid #7c3aed' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {(formData.lcb_c_code_tiers || []).map((tier, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '0.5rem 1rem' }}>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={tier.min_nt || ''} 
                      onChange={(e) => {
                        const newTiers = [...(formData.lcb_c_code_tiers || [])];
                        newTiers[index] = { ...newTiers[index], min_nt: parseFloat(e.target.value) || 0 };
                        handleChange('lcb_c_code_tiers', newTiers);
                      }}
                      style={{ width: '100px' }}
                    />
                  </td>
                  <td style={{ padding: '0.5rem 1rem' }}>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={tier.max_nt || ''} 
                      onChange={(e) => {
                        const newTiers = [...(formData.lcb_c_code_tiers || [])];
                        newTiers[index] = { ...newTiers[index], max_nt: e.target.value ? parseFloat(e.target.value) : null };
                        handleChange('lcb_c_code_tiers', newTiers);
                      }}
                      placeholder="∞"
                      style={{ width: '100px' }}
                    />
                  </td>
                  <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                    <input 
                      type="number" 
                      step="0.01"
                      className="form-input" 
                      value={tier.fee_try || ''} 
                      onChange={(e) => {
                        const newTiers = [...(formData.lcb_c_code_tiers || [])];
                        newTiers[index] = { ...newTiers[index], fee_try: parseFloat(e.target.value) || 0 };
                        handleChange('lcb_c_code_tiers', newTiers);
                      }}
                      style={{ width: '120px', textAlign: 'right', fontWeight: 600 }}
                    />
                  </td>
                  <td style={{ padding: '0.5rem 1rem', textAlign: 'center' }}>
                    <button 
                      className="action-btn action-btn-danger" 
                      onClick={() => {
                        const newTiers = (formData.lcb_c_code_tiers || []).filter((_, i) => i !== index);
                        handleChange('lcb_c_code_tiers', newTiers);
                      }}
                      style={{ padding: '0.25rem 0.5rem' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!formData.lcb_c_code_tiers || formData.lcb_c_code_tiers.length === 0) && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              Henüz kademe eklenmemiş. Varsayılan değerler kullanılacak.
            </div>
          )}
        </div>
      </div>

      {/* Demirleme Ücreti Tarifeleri */}
      <div className="card">
        <div className="card-header" style={{ background: '#fef3c7', borderBottom: '2px solid #f59e0b' }}>
          <h4 style={{ margin: 0, color: '#b45309' }}>⚓ Demirleme Ücreti Tarifeleri (TBDTDY Hariç)</h4>
        </div>
        <div className="card-body">
          <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.875rem' }}>
            GT (Gross Tonaj) başına günlük USD ücretleri. ≤7 gün için ilk tarife, &gt;7 gün için kademeli hesaplama uygulanır.
          </p>
          
          {/* Ücretsiz Gün Sayısı */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e40af' }}>
                🆓 Ücretsiz Bekleme Süresi
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input 
                  type="number" 
                  min="0"
                  className="form-input" 
                  value={formData.anchoring_free_days ?? 3} 
                  onChange={(e) => handleChange('anchoring_free_days', parseInt(e.target.value) || 0)} 
                  style={{ fontWeight: 600, width: '80px' }}
                />
                <span style={{ color: '#64748b', fontSize: '0.9rem' }}>gün</span>
                <span style={{ color: '#64748b', fontSize: '0.8rem', marginLeft: '1rem' }}>
                  (Bu süre dolmadan ücret gösterilmez. Süre dolunca ilk günden itibaren ücret hesaplanır)
                </span>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem' }}>
            {/* Türk Bayraklı */}
            <div style={{ padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
              <h5 style={{ color: '#059669', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🇹🇷 Türk Bayraklı Gemiler
              </h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>≤168 saat (≤7 gün)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>$</span>
                    <input 
                      type="number" 
                      step="0.001" 
                      className="form-input" 
                      value={formData.anchoring_rate_turkish_under_168h || 0.002} 
                      onChange={(e) => handleChange('anchoring_rate_turkish_under_168h', parseFloat(e.target.value) || 0)} 
                      style={{ fontWeight: 600, width: '100px' }}
                    />
                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>/ton/gün</span>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>&gt;168 saat (&gt;7 gün)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>$</span>
                    <input 
                      type="number" 
                      step="0.001" 
                      className="form-input" 
                      value={formData.anchoring_rate_turkish_over_168h || 0.003} 
                      onChange={(e) => handleChange('anchoring_rate_turkish_over_168h', parseFloat(e.target.value) || 0)} 
                      style={{ fontWeight: 600, width: '100px' }}
                    />
                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>/ton/gün</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Yabancı Bayraklı */}
            <div style={{ padding: '1rem', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd' }}>
              <h5 style={{ color: '#2563eb', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🌍 Yabancı Bayraklı Gemiler
              </h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>≤168 saat (≤7 gün)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>$</span>
                    <input 
                      type="number" 
                      step="0.001" 
                      className="form-input" 
                      value={formData.anchoring_rate_foreign_under_168h || 0.004} 
                      onChange={(e) => handleChange('anchoring_rate_foreign_under_168h', parseFloat(e.target.value) || 0)} 
                      style={{ fontWeight: 600, width: '100px' }}
                    />
                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>/ton/gün</span>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>&gt;168 saat (&gt;7 gün)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>$</span>
                    <input 
                      type="number" 
                      step="0.001" 
                      className="form-input" 
                      value={formData.anchoring_rate_foreign_over_168h || 0.006} 
                      onChange={(e) => handleChange('anchoring_rate_foreign_over_168h', parseFloat(e.target.value) || 0)} 
                      style={{ fontWeight: 600, width: '100px' }}
                    />
                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>/ton/gün</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Örnek Hesaplama */}
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
            <h6 style={{ color: '#475569', marginBottom: '0.5rem' }}>📝 Örnek Hesaplama (10.000 GT, 9 gün, Yabancı Bayrak)</h6>
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
              <div>İlk 7 gün: 7 × 10.000 × {formData.anchoring_rate_foreign_under_168h || 0.004} = <strong>${(7 * 10000 * (formData.anchoring_rate_foreign_under_168h || 0.004)).toFixed(2)}</strong></div>
              <div>Sonraki 2 gün: 2 × 10.000 × {formData.anchoring_rate_foreign_over_168h || 0.006} = <strong>${(2 * 10000 * (formData.anchoring_rate_foreign_over_168h || 0.006)).toFixed(2)}</strong></div>
              <div style={{ marginTop: '0.5rem', fontWeight: 600, color: '#059669' }}>
                Toplam: ${((7 * 10000 * (formData.anchoring_rate_foreign_under_168h || 0.004)) + (2 * 10000 * (formData.anchoring_rate_foreign_over_168h || 0.006))).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllTariffsSettings;
